import { useEffect, useMemo, useState } from 'react';

import { isAssetDisabledAnnotations } from 'src/data/asset';
import { parseHistogramData } from 'src/data/variation';
import { calculateBlockedReason } from 'src/data/utils';
import { getToolBriefType } from 'src/data/generationTool';
import { prepareAnnotations } from 'src/data/annotation';
import { queryViewSharedDocumentVariationDetails } from 'src/graphql/sharing';
import { useMainActions } from '../../../../../../store';

export const useDetailsData = (variation, text) => {
  const mainActions = useMainActions();

  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);

  const variationId = variation?.id;
  const variationGenerationTool = variation?.generationTool;
  const variationAssetType = variation?.assetType;

  useEffect(() => {
    const shareDocumentId = mainActions.getShareDocumentId();

    const fetch = async () => {
      setLoading(true);
      try {
        const result = await queryViewSharedDocumentVariationDetails({
          shareDocumentId,
          variationId,
          text,
        });
        setResultData(result);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [mainActions, variationId, text]);

  const scoreData = useMemo(
    () => ({
      score: resultData?.scoring?.score,
      basedOn: resultData?.scoring?.engagementScoreSupportSize,
      loading,
    }),
    [resultData, loading]
  );

  const histogramsData = useMemo(() => {
    const histograms = parseHistogramData(resultData?.scoring);
    return {
      age: histograms?.ages,
      gender: histograms?.genders,
      loading,
    };
  }, [resultData, loading]);

  const blockedReason = useMemo(
    () =>
      calculateBlockedReason({
        language: resultData?.scoring?.language,
        text,
        generationTool: variationGenerationTool,
      }),
    [resultData, text, variationGenerationTool]
  );

  const annotationsData = useMemo(() => {
    const briefType = getToolBriefType(variationGenerationTool);

    const preparedAnnotations = !isAssetDisabledAnnotations(variationAssetType)
      ? prepareAnnotations(resultData?.annotations || [], loading, {
          briefType,
        })
      : [];

    return {
      annotations: preparedAnnotations,
      loading,
    };
  }, [loading, resultData?.annotations, variationAssetType, variationGenerationTool]);

  const sensitivityData = useMemo(() => {
    return {
      sensitivity: resultData?.validations,
      loading,
    };
  }, [resultData, loading]);

  return { scoreData, histogramsData, blockedReason, annotationsData, sensitivityData };
};
